import { useEffect, useMemo, useState } from "react";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { logsApi, logsApiConfigured } from "../lib/logsApi";
import { useAsync } from "../lib/hooks";
import { formatMs } from "../lib/format";
import { useI18n } from "../lib/i18n";
import type { DomainCheckLog } from "../lib/types";
import { Card, Empty, ErrorBox, Loading } from "../components/ui";
import { DataTable } from "../components/DataTable";

/** Kết quả log — theo đúng quy tắc server dùng để quyết định forward sang bên thứ 3
 * (xem `Logs.md`): `status` là `ok` (không phân biệt hoa/thường) hoặc `status_code`
 * trong 200–399 thì tính là thành công, còn lại là lỗi. */
function GeoTooltip({ geo }: { geo: Record<string, unknown> | null }) {
  const { t } = useI18n();
  if (!geo) return <span className="muted">{t("logs.geoNone")}</span>;
  const conn = geo.connection as { isp?: string } | undefined;
  const tz = geo.timezone as { id?: string } | undefined;
  const parts = [
    geo.ip ? `IP ${geo.ip}` : null,
    conn?.isp ? `ISP ${conn.isp}` : null,
    geo.city ? `City ${geo.city}` : null,
    tz?.id ? `TZ ${tz.id}` : null,
  ].filter(Boolean);
  return (
    <Tooltip title={JSON.stringify(geo, null, 2)}>
      <span className="mono">{parts.length ? parts.join(", ") : t("logs.geoNone")}</span>
    </Tooltip>
  );
}

function ResultChip({ log }: { log: DomainCheckLog }) {
  const { t } = useI18n();
  const ok =
    (log.status ?? "").toLowerCase() === "ok" ||
    (log.status_code != null && log.status_code >= 200 && log.status_code < 400);
  return (
    <Chip
      size="small"
      variant="outlined"
      color={ok ? "success" : "error"}
      label={ok ? t("logs.resultSuccess") : t("logs.resultError")}
    />
  );
}

export function Logs() {
  const { t, fmtDate } = useI18n();
  const [domain, setDomain] = useState("");
  const [ip, setIp] = useState("");
  const [location, setLocation] = useState("");
  const [brand, setBrand] = useState("");
  const [result, setResult] = useState<"" | "success" | "error">("");
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });

  const configured = logsApiConfigured();

  const logs = useAsync(
    () =>
      logsApi.list({
        domain: domain || undefined,
        ip: ip || undefined,
        location: location || undefined,
        brand: brand || undefined,
        result: result || undefined,
        limit: paginationModel.pageSize,
        offset: paginationModel.page * paginationModel.pageSize,
      }),
    [domain, ip, location, brand, result, paginationModel.page, paginationModel.pageSize],
  );

  // Đổi bộ lọc thì trang cũ không còn nghĩa gì.
  useEffect(() => {
    setPaginationModel(m => ({ ...m, page: 0 }));
  }, [domain, ip, location, brand, result]);

  const columns = useMemo<GridColDef<DomainCheckLog>[]>(
    () => [
      {
        field: "timestamp",
        headerName: t("logs.receivedAt"),
        flex: 0.9,
        minWidth: 150,
        valueFormatter: (v: string) => fmtDate(v),
      },
      { field: "brand", headerName: t("table.brand"), flex: 0.5, minWidth: 90 },
      { field: "domain", headerName: t("table.domain"), flex: 1.1, minWidth: 160 },
      {
        field: "result",
        headerName: t("logs.result"),
        flex: 0.5,
        minWidth: 100,
        sortable: false,
        renderCell: ({ row }) => <ResultChip log={row} />,
      },
      {
        field: "status_code",
        headerName: t("table.http"),
        type: "number",
        flex: 0.4,
        minWidth: 75,
        valueFormatter: (v: number | null) => v ?? "—",
      },
      {
        field: "latency_ms",
        headerName: t("table.response"),
        type: "number",
        flex: 0.5,
        minWidth: 95,
        valueFormatter: (v: number | null) => formatMs(v),
      },
      {
        field: "ip",
        headerName: t("table.ip"),
        flex: 0.6,
        minWidth: 110,
        valueGetter: (v: string | null) => v ?? "—",
      },
      {
        field: "location",
        headerName: t("logs.location"),
        flex: 0.5,
        minWidth: 90,
        valueGetter: (v: string | null) => v ?? "—",
      },
      {
        field: "cms_name",
        headerName: t("logs.cmsName"),
        flex: 0.6,
        minWidth: 110,
        valueGetter: (v: string | null) => v ?? "—",
      },
      {
        field: "checked_at",
        headerName: t("logs.checkedAt"),
        flex: 0.9,
        minWidth: 150,
        valueFormatter: (v: string | null) => (v ? fmtDate(v) : "—"),
      },
      {
        field: "geo",
        headerName: t("logs.geo"),
        flex: 1.4,
        minWidth: 200,
        sortable: false,
        renderCell: ({ row }) => <GeoTooltip geo={row.geo} />,
      },
    ],
    [t, fmtDate],
  );

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>{t("logs.title")}</h1>
          <p className="muted">{t("logs.subtitle")}</p>
        </div>
      </header>

      {!configured ? (
        <Card>
          <ErrorBox message={t("logs.notConfigured")} />
        </Card>
      ) : (
        <Card>
          <Stack
            direction="row"
            spacing={2}
            useFlexGap
            sx={{ flexWrap: "wrap", mb: 1.5, "& > *": { minWidth: 160, flex: "1 1 160px" } }}
          >
            <TextField
              size="small"
              label={t("table.domain")}
              value={domain}
              onChange={e => setDomain(e.target.value)}
            />
            <TextField
              size="small"
              label={t("table.ip")}
              value={ip}
              onChange={e => setIp(e.target.value)}
            />
            <TextField
              size="small"
              label={t("logs.location")}
              value={location}
              onChange={e => setLocation(e.target.value)}
            />
            <Tooltip title={t("logs.brandHint")}>
              <TextField
                size="small"
                label={t("table.brand")}
                value={brand}
                onChange={e => setBrand(e.target.value)}
              />
            </Tooltip>
            <TextField
              select
              size="small"
              label={t("logs.result")}
              value={result}
              onChange={e => setResult(e.target.value as "" | "success" | "error")}
            >
              <MenuItem value="">{t("logs.resultAll")}</MenuItem>
              <MenuItem value="success">{t("logs.resultSuccess")}</MenuItem>
              <MenuItem value="error">{t("logs.resultError")}</MenuItem>
            </TextField>
          </Stack>

          {logs.loading && !logs.data ? (
            <Loading />
          ) : logs.error ? (
            <ErrorBox message={logs.error} />
          ) : (logs.data?.logs.length ?? 0) === 0 ? (
            <Empty message={t("logs.empty")} />
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t("logs.totalCount", { n: logs.data?.total ?? 0 })}
              </Typography>
              <DataTable
                rows={logs.data?.logs ?? []}
                columns={columns}
                getRowId={row => row.id}
                loading={logs.loading}
                paginationMode="server"
                rowCount={logs.data?.total ?? 0}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[25, 50, 100, 200]}
              />
            </>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
            {t("logs.thirdParty")}
          </Typography>
        </Card>
      )}
    </div>
  );
}
